import React, { useLayoutEffect, useState } from "react";
import { useSelector } from "react-redux";
import { motion } from "framer-motion";
import {
  SUPPORTED_REGIONS,
  CURRENCY_SIGN,
  CURRENCY_MAPPING,
  PRODUCT_VARIANT_ABBREVIATIONS,
} from "../../../shared/enums";
import { selectRegionalPricesData } from "../../features/regionalPrices/regionalPricesSlice";
import { getUnitPrice } from "../../features/regionalPrices/regionalPricesHelpers";
import { getAir3BuyUrl } from "../../static-data/shopUrls";
import { useIsMobile } from "../../hooks";
import milaImg from "assets/images/landingAir/mila-order/milaair-order-bg.jpg";
import milaImgSm from "assets/images/landingAir/mila-order/milaair-order-bg-sm.jpg";

const AIR_MINI_WAITLIST_URL = "https://milamarketing.typeform.com/preordermini";
/**
 * @returns {JSX.Element}
 * @constructor
 */

const data = {
  airMini: {
    US: {
      offer: 219,
      regular: 219,
      dimentions: (
        <>
          714 ft<sup>2</sup>
        </>
      ),
    },
    CA: {
      offer: 270,
      dimentions: (
        <>
          66m<sup>2</sup>
        </>
      ),
    },
  },
  air: {
    US: {
      offer: 399,
      dimentions: (
        <>
          1000 ft<sup>2</sup>
        </>
      ),
    },
    CA: {
      offer: 470,
      dimentions: (
        <>
          93 m<sup>2</sup>
        </>
      ),
    },
    AU: {
      offer: 599,
      dimentions: (
        <>
          1000 ft<sup>2</sup>
        </>
      ),
    },
  },
};

const MilaOrder = ({ localization }) => {
  const { region } = localization;
  const regional = SUPPORTED_REGIONS[region] || SUPPORTED_REGIONS.US;
  const isAu = regional === SUPPORTED_REGIONS.AU;
  const air3BuyUrl = getAir3BuyUrl(regional);
  const currencySign = CURRENCY_SIGN[regional] || CURRENCY_SIGN.US;
  const currencyCode = CURRENCY_MAPPING[regional] || CURRENCY_MAPPING.US;
  const regionalPrices = useSelector(selectRegionalPricesData);
  const airPrice = getUnitPrice(
    regionalPrices,
    PRODUCT_VARIANT_ABBREVIATIONS.BASIC_BREATHER,
  );
  const airMiniPrice = getUnitPrice(
    regionalPrices,
    PRODUCT_VARIANT_ABBREVIATIONS.BASIC_BREATHER_MINI,
  );

  const isMobile = useIsMobile(769);
  const [isBg, setIsBg] = useState(milaImg);

  useLayoutEffect(() => {
    setIsBg(isMobile ? milaImgSm : milaImg);
  }, [isMobile]);

  return (
    <section className="mila-order">
      <div
        className="mila-order__wrapper"
        style={{ backgroundImage: `url(${isBg})` }}
      >
        <div className="container">
          <div className="mila-order__header">
            <motion.h2
              className="mila-order__title"
              initial={{ opacity: 0, y: 20 }}
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0 }}
              variants={{
                visible: { opacity: 1, y: 0 },
                hidden: { opacity: 0, y: 20 },
              }}
            >
              Bring Mila <br />
              into your airspace.
            </motion.h2>
            <motion.div
              className="mila-order__short-description"
              initial={{ opacity: 0, y: 20 }}
              whileInView="visible"
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.01 }}
              variants={{
                visible: { opacity: 1, y: 0 },
                hidden: { opacity: 0, y: 20 },
              }}
            >
              <p>We guarantee you’ll become a Milieber, or your money back.</p>
            </motion.div>
          </div>
        </div>
        <div className="mila-order__overflow">
          <div className="mila-order__overflow-container">
            <div className="mila-order__grid">
              <motion.div
                className="mila-order__card mila-order__card--mini"
                initial={{ opacity: 0, y: 20 }}
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0 }}
                variants={{
                  visible: { opacity: 1, y: 0 },
                  hidden: { opacity: 0, y: 20 },
                }}
              >
                <div className="mila-order__card-content">
                  <h3 className="mila-order__card-title">Mila Air Mini</h3>
                  <h4 className="mila-order__card-subtitle">
                    Ideal for medium-sized rooms
                  </h4>
                  <p className="mila-order__card-description">
                    {isMobile ? (
                      <>
                        Our compact, colorful addition, perfect for medium rooms
                        up to{" "}
                        {data.airMini[regional]
                          ? data.airMini[regional].dimentions
                          : data.airMini[SUPPORTED_REGIONS.US].dimentions}
                        .
                      </>
                    ) : (
                      <>
                        Our more compact and colorful addition to the family.
                        Perfect for medium-sized rooms, up to{" "}
                        {data.airMini[regional]
                          ? data.airMini[regional].dimentions
                          : data.airMini[SUPPORTED_REGIONS.US].dimentions}
                        .
                      </>
                    )}
                  </p>
                  {!isAu && (
                    <p className="mila-order__card-price">
                      <strong>
                        {`Starting at ${currencySign}${airMiniPrice || ""} ${currencyCode} `}
                        {/*
                        <del>
                          ${data.airMini[regional]
                            ? data.airMini[regional].regular
                            : data.airMini[SUPPORTED_REGIONS.US].regular}
                        </del>
                        */}
                      </strong>
                    </p>
                  )}
                </div>
                <div className="mila-order__card-footer">
                  <a
                    href={
                      isAu
                        ? AIR_MINI_WAITLIST_URL
                        : "https://buy.milacares.com/"
                    }
                    className="button mila-order__card-button"
                  >
                    {isAu ? "Join the Waitlist" : "Order Now"}
                  </a>
                </div>
              </motion.div>
              <motion.div
                className="mila-order__card mila-order__card--3"
                initial={{ opacity: 0, y: 20 }}
                whileInView="visible"
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0 }}
                variants={{
                  visible: { opacity: 1, y: 0 },
                  hidden: { opacity: 0, y: 20 },
                }}
              >
                <div className="mila-order__card-content">
                  <h3 className="mila-order__card-title">Mila Air 3</h3>
                  <h4 className="mila-order__card-subtitle">
                    Ideal for larger rooms and spaces
                  </h4>
                  <p className="mila-order__card-description">
                    Our OG air purifier with the muscle to handle <br /> larger
                    spaces, up to{" "}
                    {data.air[regional]
                      ? data.air[regional].dimentions
                      : data.air[SUPPORTED_REGIONS.US].dimentions}
                    .
                  </p>
                  <p className="mila-order__card-price">
                    <strong>
                      {`Starting at ${currencySign}${airPrice || ""} ${currencyCode} `}
                      {/*
                      <del>
                        ${data.air[regional]
                          ? data.air[regional].regular
                          : data.air[SUPPORTED_REGIONS.US].regular}
                      </del>
                      */}
                    </strong>
                  </p>
                </div>
                <div className="mila-order__card-footer">
                  <a
                    href={isAu ? air3BuyUrl : "https://milacares.com/buy"}
                    className="button mila-order__card-button"
                  >
                    Preorder Now
                  </a>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default MilaOrder;
